import type {
  IAudioContext,
  IMediaStreamAudioSourceNode,
} from 'standardized-audio-context';
import type { StateCreator } from 'zustand';
import { captureFlags } from '../configure/flags-facade';
import { captureUserContext } from '../configure/user-context-facade';
import { getSafeErrorFields, isDeviceInUseDOMException } from '../errors';
import { DeviceInUseError, TabSharingError } from '../types';
import type { AudioGraphStore } from './types';

export interface SourceSlice {
  micSourceStream: MediaStream | null;
  tabSourceStream: MediaStream | null;
  micSourceNode: IMediaStreamAudioSourceNode<IAudioContext> | null;
  tabSourceNode: IMediaStreamAudioSourceNode<IAudioContext> | null;
  createMicSource: (signal?: AbortSignal) => Promise<void>;
  createTabSource: (signal?: AbortSignal) => Promise<void>;
  cleanupResourcesIfUnused: () => void;
  cleanupMicSource: (disconnectAnalyzer?: boolean) => void;
  cleanupTabSource: (disconnectAnalyzer?: boolean) => void;
  videoStream: MediaStream | null;
}

export const createSourceSlice: StateCreator<AudioGraphStore, [], [], SourceSlice> = (
  set,
  get
) => ({
  micSourceStream: null,
  tabSourceStream: null,
  micSourceNode: null,
  tabSourceNode: null,
  videoStream: null,

  cleanupMicSource: (disconnectAnalyzer: boolean = true) => {
    const sourceNode = get().micSourceNode;
    const sourceStream = get().micSourceStream;
    try {
      if (disconnectAnalyzer) sourceNode?.disconnect(get().micAnalyzer!);
      sourceNode?.disconnect();
    } catch (err) {
      console.trace(err);
      get().logError({
        event_type: 'CLEANUP_MIC_SOURCE_ERROR',
        details: getSafeErrorFields(err),
      });
    }
    try {
      sourceStream?.getTracks().forEach((track) => track.stop());
    } catch (err) {
      get().logError({
        event_type: 'CLEANUP_MIC_STREAM_ERROR',
        details: getSafeErrorFields(err),
      });
    }
  },

  cleanupTabSource: (disconnectAnalyzer: boolean = true) => {
    const sourceNode = get().tabSourceNode;
    const sourceStream = get().tabSourceStream;
    const videoStream = get().videoStream;
    try {
      if (disconnectAnalyzer) sourceNode?.disconnect(get().tabAnalyzer!);
      sourceNode?.disconnect();
    } catch (err) {
      get().logError({
        event_type: 'CLEANUP_TAB_SOURCE_ERROR',
        details: getSafeErrorFields(err),
      });
    }
    try {
      sourceStream?.getTracks().forEach((track) => track.stop());
      videoStream?.getTracks().forEach((track) => track.stop());
    } catch (err) {
      get().logError({
        event_type: 'CLEANUP_TAB_STREAM_ERROR',
        details: getSafeErrorFields(err),
      });
    }
  },

  createMicSource: async (signal) => {
    const enableDefaultTelehealthMode = captureFlags.isDefaultTelehealthModeEnabled();
    await get().loadInputDevices();
    if (signal?.aborted) throw signal.reason;
    const userData = captureUserContext.getCurrentUser();

    const deviceId = get().device?.deviceId;
    const audioConstraints: MediaTrackConstraints = {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      echoCancellation: true,
    };

    if (enableDefaultTelehealthMode && userData && userData.session_settings) {
      audioConstraints.echoCancellation =
        !userData.session_settings.default_telehealth_mode;
    }

    let micSourceStream: MediaStream;
    try {
      micSourceStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
    } catch (err) {
      if (isDeviceInUseDOMException(err)) {
        // The typed error replaces the DOMException, so capture the original
        // name here — it's the only place NotReadableError (Chrome/Edge) vs
        // AbortError (Firefox) stays distinguishable for Datadog.
        get().logError({
          event_type: 'MIC_DEVICE_IN_USE',
          details: { name: err.name, message: err.message },
        });
        get().addError('DEVICE_IN_USE');
        throw new DeviceInUseError('microphone device in use', { cause: err });
      }
      throw err;
    }
    if (signal?.aborted) {
      micSourceStream.getTracks().forEach((track) => track.stop());
      throw signal.reason;
    }
    get().clearError('DEVICE_IN_USE');

    const micSourceNode = get().audioContext!.createMediaStreamSource(micSourceStream);
    micSourceNode.connect(get().recorderDestinationNode!);
    if (get().micAnalyzer) micSourceNode.connect(get().micAnalyzer!);
    set({ micSourceStream, micSourceNode });
  },

  createTabSource: async (signal) => {
    if (signal?.aborted) throw signal.reason;
    // Chrome defaults selfBrowserSurface to 'exclude', hiding the tab Heidi runs
    // in so users can't pick their current tab as the telehealth audio source.
    // Gated rollout: when on, include it in the picker; off preserves the default.
    const selfBrowserSurface = captureFlags.isTelehealthAllowCurrentTabEnabled()
      ? 'include'
      : 'exclude';

    const videoStream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: 1,
        height: 1,
        frameRate: 1,
        displaySurface: 'browser',
      },
      audio: {
        channelCount: 2,
        noiseSuppression: false,
        autoGainControl: false,
        echoCancellation: false,
      },
      // Experimental properties: MDN defines them but they throw where they are
      // new, and browsers that do not know them ignore them.
      // @ts-expect-error see above
      systemAudio: 'exclude',
      windowAudio: 'exclude',
      selfBrowserSurface,
    });

    if (signal?.aborted) {
      videoStream.getTracks().forEach((track) => track.stop());
      throw signal.reason;
    }

    if (videoStream.getAudioTracks().length == 0) {
      videoStream.getTracks().forEach((t) => t.stop());
      throw new TabSharingError('No audio tracks in tab stream');
    }

    const tabSourceStream = new MediaStream();
    videoStream
      .getAudioTracks()
      .forEach((audioTrack) => tabSourceStream.addTrack(audioTrack));

    const tabSourceNode = get().audioContext!.createMediaStreamSource(tabSourceStream);
    tabSourceNode.connect(get().recorderDestinationNode!);
    if (get().tabAnalyzer) tabSourceNode.connect(get().tabAnalyzer!);
    set({ tabSourceStream, tabSourceNode, videoStream });
  },

  cleanupResourcesIfUnused: () => {
    const { listeners, micAnalyzerUsers, tabAnalyzerUsers } = get();

    if (listeners.length === 0) {
      if (micAnalyzerUsers.size === 0) {
        get().cleanupMicSource();
      }
      if (tabAnalyzerUsers.size === 0) {
        get().cleanupTabSource();
      }
    }
  },
});
